import type { AnalyticsTrackOptions } from '~analytics/types/analyticsProvider';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

import { authStateFor } from '~/utils/analytics/authState';

/**
 * The route *pattern* the student is on — `/coach/jobs/:jobSlug`, not `/coach/jobs/plumber`.
 *
 * `pageName` alone stopped being enough once the catch-all routes collapsed several surfaces into
 * one name, so events need something that says which shape of page they came from. It is the
 * matched record's path rather than `path`/`fullPath` deliberately: params stay unresolved, so
 * there is no per-slug cardinality to blow up a breakdown and no query string to carry anything
 * personal into a provider.
 *
 * The leaf record carries the whole pattern, parents included; nothing matched means there is no
 * pattern to report.
 */
export const matchedRoutePath = (route: RouteLocationNormalizedLoaded): string | null =>
  route.matched[route.matched.length - 1]?.path ?? null;

export const useAnalytics = () => {
  const { user, accountStudentRole, isGhostStudent, isLessonGhost } = useAuth();
  const configStore = useConfigStore();

  const baseEventData = () => {
    const route = useRoute();
    const { appType } = usePlatformAppConfig(route.path);

    return {
      cluster: configStore.cluster?.name || 'unknown',
      country: configStore.publicConfig?.country?.code ?? 'unknown',
      pageName: route.name,
      routePath: matchedRoutePath(route),
      product: appType,
      evaluatedFlags: JSON.stringify(useFeatureFlags().getEvaluatedFlags()),
      authState: authStateFor(user.value),
      ...(user.value
        ? {
            userId: user.value?.id,
            classYear: accountStudentRole.value?.classYear ?? null,
            isGhostUser: isGhostStudent.value,
            externalId: user.value.externalId ?? null,
          }
        : {}),
    };
  };

  const analyticsServiceProvider = useAnalyticsServiceProvider(baseEventData);
  const suppression = useAnalyticsSuppressionStore();

  // Skip all tracking while suppressed or the user is a lesson participant ghost.
  const track = (eventName: string, eventData?: object, options?: AnalyticsTrackOptions) => {
    if (suppression.isSuppressed || isLessonGhost.value) {
      return;
    }
    analyticsServiceProvider.track(eventName, eventData, options);
  };

  // Identity, not just events. A participant has no `externalId` to send, but an identify call
  // with no id still opens a profile against the provider's own anonymous id, joined to whatever
  // comes next on the device.
  const identify = (guid: string | undefined) => {
    if (isLessonGhost.value) {
      return;
    }
    analyticsServiceProvider.identify(guid);
  };

  // The plugin syncs `isAnywayUser` off the signed-in email on every user change, and a property
  // write opens a profile just as an event does.
  const setUserProperties = (properties: Record<string, unknown>) => {
    if (isLessonGhost.value) {
      return;
    }
    analyticsServiceProvider.setUserProperties(properties);
  };

  // The spread comes first so every override below wins. `$analytics` is this object: the plugin
  // provides what this composable returns, and `useAnalyticsService()` hands it to around 120 call
  // sites, so these three are the whole client-side write surface rather than one of two paths.
  return {
    ...analyticsServiceProvider,
    track,
    identify,
    setUserProperties,
  };
};
